import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle, SpinnerGap, XCircle, Heart } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const POLL_MAX = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("polling");
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sid) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setInfo(data); setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      tries.current += 1;
      if (tries.current >= POLL_MAX) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);

  return (
    <div className="mx-auto grid max-w-md place-items-center px-5 py-24 text-center" data-testid="payment-success-page">
      {state === "polling" && <><SpinnerGap size={44} className="animate-spin text-secondary" /><p className="mt-4 text-muted-foreground">Confirming your contribution…</p></>}
      {state === "paid" && (
        <>
          <div className="grid h-16 w-16 place-items-center rounded-full bg-secondary/15"><CheckCircle size={40} weight="fill" className="text-secondary" /></div>
          <h1 className="mt-5 font-heading text-3xl font-light">Thank you for supporting AlumniPulse™</h1>
          <p className="mt-3 text-muted-foreground">Your {info?.recurring ? "recurring support" : "contribution"} helps us connect generations and preserve the memories that matter. You're now part of the journey. <Heart size={15} weight="fill" className="inline text-secondary" /></p>
          <div className="mt-7 flex gap-3"><Link to="/"><Button className="rounded-full">Back home</Button></Link><Link to="/memory-lane"><Button variant="outline" className="rounded-full">Contribute a memory</Button></Link></div>
        </>
      )}
      {state === "pending" && (
        <>
          <SpinnerGap size={44} className="text-muted-foreground" />
          <h1 className="mt-4 font-heading text-2xl font-light">Almost there</h1>
          <p className="mt-2 text-sm text-muted-foreground">Your payment is processing. It may take a moment to confirm — you'll receive an email receipt from Stripe.</p>
          <Link to="/"><Button variant="outline" className="mt-6 rounded-full">Back home</Button></Link>
        </>
      )}
      {state === "error" && (
        <>
          <XCircle size={44} weight="fill" className="text-destructive" />
          <h1 className="mt-4 font-heading text-2xl font-light">We couldn't confirm your contribution</h1>
          <p className="mt-2 text-sm text-muted-foreground">No charge may have been made. Please try again or contact support.</p>
          <Link to="/support"><Button variant="outline" className="mt-6 rounded-full">Back to Support</Button></Link>
        </>
      )}
    </div>
  );
}
